import { Alert, Button, Card, Col, Form, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import type { IMappingValidationResult } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import {
    EA_WASTE_RETURN_DATA_SOURCES_SECTION_LABEL,
    EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS,
} from "./eaWasteReturnListSourceDictionary";
import {
    EA_WASTE_RETURN_FIELD_SECTION_LABELS,
    EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS,
    type EaWasteReturnFieldSection,
} from "./eaWasteReturnFieldDictionary";
import type { IEaWasteReturnListSourceRow } from "./listSourceMappingAdapter";
import { buildMappingWithListSources } from "./listSourceMappingAdapter";
import {
    MAPPING_ADDRESS_FIELD_LABEL,
    MAPPING_ADDRESS_FIELD_PLACEHOLDER,
    MAPPING_ADDRESS_FIELD_TOOLTIP,
} from "./mappingAddressFieldLabels";
import {
    mapMappingToFlatRows,
    type IEaWasteReturnFieldRow,
} from "./wasteReturnFieldMappingAdapter";

export interface WasteReturnFieldMappingStepProps {
    fieldRows: IEaWasteReturnFieldRow[];
    listSourceRows: IEaWasteReturnListSourceRow[];
    sheetOptions: string[];
    hasUnsavedChanges: boolean;
    disabled?: boolean;
    saving?: boolean;
    continuing?: boolean;
    structuralValidation: IMappingValidationResult | null;
    onFieldRowsChange: (rows: IEaWasteReturnFieldRow[]) => void;
    onListSourceRowsChange: (rows: IEaWasteReturnListSourceRow[]) => void;
    onSaveMapping: () => void;
    onContinue: () => void;
}

const SECTION_ORDER: EaWasteReturnFieldSection[] = [
    "siteInformation",
    "technicalCompetence",
    "wasteReceived",
    "wasteRemoved",
];

function AddressFieldLabel() {
    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{MAPPING_ADDRESS_FIELD_TOOLTIP}</Tooltip>}>
            <span className="text-decoration-underline text-decoration-style-dotted cursor-help">
                {MAPPING_ADDRESS_FIELD_LABEL}
            </span>
        </OverlayTrigger>
    );
}

export function WasteReturnFieldMappingStep({
    fieldRows,
    listSourceRows,
    sheetOptions,
    hasUnsavedChanges,
    disabled,
    saving,
    continuing,
    structuralValidation,
    onFieldRowsChange,
    onListSourceRowsChange,
    onSaveMapping,
    onContinue,
}: WasteReturnFieldMappingStepProps) {
    const updateFieldRow = (fieldKey: string, patch: Partial<IEaWasteReturnFieldRow>) => {
        onFieldRowsChange(fieldRows.map((row) => (row.fieldKey === fieldKey ? { ...row, ...patch } : row)));
    };

    const updateListSourceRow = (rowKey: string, patch: Partial<IEaWasteReturnListSourceRow>) => {
        onListSourceRowsChange(listSourceRows.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
    };

    const structuralErrors = structuralValidation?.errors ?? [];
    const showStructuralErrors = structuralValidation && !structuralValidation.isValid;

    return (
        <Card className="mb-4">
            <Card.Header>Map fields</Card.Header>
            <Card.Body>
                <p className="text-muted small">
                    Tell us where each export field lives in the workbook. Worksheet names come from the uploaded file; cell addresses are defaults or what you type here.
                </p>

                {hasUnsavedChanges && (
                    <Alert variant="warning" className="py-2 small">
                        You have unsaved mapping changes. Save before leaving this step or they will be lost on reload.
                    </Alert>
                )}

                {showStructuralErrors && (
                    <Alert variant="danger" className="small">
                        <strong>Fix these before continuing:</strong>
                        <ul className="mb-0 mt-2">
                            {structuralErrors.map((issue, index) => (
                                <li key={`${issue.code}-${index}`}>{issue.message}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {SECTION_ORDER.map((section) => {
                    const sectionRows = fieldRows.filter((row) => row.section === section);
                    if (sectionRows.length === 0) return null;

                    return (
                        <div key={section} className="mb-4">
                            <h3 className="h6">{EA_WASTE_RETURN_FIELD_SECTION_LABELS[section]}</h3>
                            {sectionRows.map((row) => {
                                const definition = EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS.find(
                                    (item) => item.fieldKey === row.fieldKey
                                );

                                return (
                                    <Row className="g-3 mb-3 pb-3 border-bottom align-items-end" key={row.fieldKey}>
                                        <Col md={4}>
                                            <strong>{row.label}</strong>
                                            {definition?.regFieldKey && (
                                                <div className="text-muted small">List field: {definition.regFieldKey}</div>
                                            )}
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Worksheet</Form.Label>
                                                <Form.Select
                                                    value={row.sheet}
                                                    disabled={disabled}
                                                    onChange={(e) => updateFieldRow(row.fieldKey, { sheet: e.target.value })}
                                                >
                                                    <option value="">Select sheet…</option>
                                                    {sheetOptions.map((sheet) => (
                                                        <option key={sheet} value={sheet}>
                                                            {sheet}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    <AddressFieldLabel />
                                                </Form.Label>
                                                <Form.Control
                                                    value={row.address}
                                                    disabled={disabled}
                                                    onChange={(e) => updateFieldRow(row.fieldKey, { address: e.target.value })}
                                                    placeholder={MAPPING_ADDRESS_FIELD_PLACEHOLDER}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                );
                            })}
                        </div>
                    );
                })}

                <div className="mb-4">
                    <h3 className="h6">{EA_WASTE_RETURN_DATA_SOURCES_SECTION_LABEL}</h3>
                    <p className="text-muted small">
                        Where the workbook stores allowed values for regulatory list comparison. Leave a row blank to use automatic detection.
                    </p>
                    {listSourceRows.map((row) => {
                        const definition = EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS.find((item) => item.rowKey === row.rowKey);
                        return (
                            <Row className="g-3 mb-3 pb-3 border-bottom align-items-end" key={row.rowKey}>
                                <Col md={4}>
                                    <strong>{row.label}</strong>
                                    <div className="text-muted small">List field: {row.regFieldKey}</div>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Worksheet</Form.Label>
                                        <Form.Select
                                            value={row.sheet}
                                            disabled={disabled}
                                            onChange={(e) => updateListSourceRow(row.rowKey, { sheet: e.target.value })}
                                        >
                                            <option value="">Select sheet…</option>
                                            {sheetOptions.map((sheet) => (
                                                <option key={sheet} value={sheet}>
                                                    {sheet}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>
                                            <AddressFieldLabel />
                                        </Form.Label>
                                        <Form.Control
                                            value={row.address}
                                            disabled={disabled}
                                            onChange={(e) => updateListSourceRow(row.rowKey, { address: e.target.value })}
                                            placeholder={definition?.defaultAddress ?? MAPPING_ADDRESS_FIELD_PLACEHOLDER}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        );
                    })}
                </div>

                <Row className="g-2 justify-content-end">
                    <Col xs="auto">
                        <Button variant="outline-primary" onClick={onSaveMapping} disabled={disabled || saving}>
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving…
                                </>
                            ) : (
                                "Save mapping"
                            )}
                        </Button>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" onClick={onContinue} disabled={disabled || continuing}>
                            {continuing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving & extracting list values…
                                </>
                            ) : (
                                "Continue to synchronise"
                            )}
                        </Button>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

export function buildMappingFromFieldRows(
    fieldRows: IEaWasteReturnFieldRow[],
    listSourceRows: IEaWasteReturnListSourceRow[],
    baseMapping?: Parameters<typeof buildMappingWithListSources>[2]
) {
    return buildMappingWithListSources(fieldRows, listSourceRows, baseMapping);
}

export function buildFieldRowsFromMapping(mapping: Parameters<typeof mapMappingToFlatRows>[0]) {
    return mapMappingToFlatRows(mapping);
}

export { buildMappingWithListSources };
